import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { getInsightOperators, getProductionAudit, getAuditHistory } from '../services/api';
import { toast } from 'sonner';
import { Sparkles, Star, Loader2, AlertTriangle, TrendingDown, Lightbulb, Gauge, Trophy, History } from 'lucide-react';

const riskColor = (r) => ({ high: 'bg-red-100 text-red-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-green-100 text-green-700' }[r] || 'bg-gray-100 text-gray-700');

const rankStyle = (i) => i === 0 ? 'bg-amber-400 text-white' : i === 1 ? 'bg-slate-300 text-slate-800' : i === 2 ? 'bg-orange-400 text-white' : 'bg-muted text-muted-foreground';

const Stars = ({ n }) => {
  const arr = [1, 2, 3, 4, 5];
  return <span className="inline-flex gap-0.5">{arr.map((i) => <Star key={i} size={14} className={i <= n ? 'text-amber-500 fill-amber-500' : 'text-muted-foreground/40'} />)}</span>;
};

const ProductionInsightPage = () => {
  const [operators, setOperators] = useState([]);
  const [loadingOps, setLoadingOps] = useState(true);
  const [period, setPeriod] = useState('all');
  const [audit, setAudit] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const [history, setHistory] = useState([]);

  const fetchOps = useCallback(async () => {
    setLoadingOps(true);
    try { const res = await getInsightOperators(period === 'week' ? 'week' : undefined); setOperators(res.data); }
    catch (e) { toast.error('Failed to load operator performance'); }
    finally { setLoadingOps(false); }
  }, [period]);

  const fetchHistory = useCallback(async () => {
    try { const res = await getAuditHistory(); setHistory(res.data); } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { fetchOps(); }, [fetchOps]);
  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const runAudit = async () => {
    setAuditing(true);
    try { const res = await getProductionAudit(); setAudit(res.data); fetchHistory(); toast.success('Audit generated'); }
    catch (e) { toast.error('Could not generate the audit'); }
    finally { setAuditing(false); }
  };

  const delays = audit ? audit.predicted_delays || [] : [];
  const slipping = audit ? audit.slipping_stages || [] : [];
  const recs = audit ? audit.recommendations || [] : [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="production-insight-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Sparkles size={22} className="text-accent" />Production Insight</h1>
          <p className="text-muted-foreground">AI-assessed delays, a real-time audit, and operator performance</p>
        </div>
        <Button className="btn-accent" onClick={runAudit} disabled={auditing} data-testid="generate-audit-btn">
          {auditing ? <><Loader2 size={16} className="mr-2 animate-spin" />Analysing…</> : <><Sparkles size={16} className="mr-2" />Generate AI Audit</>}
        </Button>
      </div>

      {/* AI Audit */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Gauge size={18} className="text-accent" />Real-time Audit</CardTitle></CardHeader>
        <CardContent>
          {!audit ? (
            <p className="text-sm text-muted-foreground">Tap "Generate AI Audit" to assess current jobs for likely delays and get recommendations.</p>
          ) : (
            <div className="space-y-4" data-testid="audit-result">
              <p className="text-sm">{audit.summary}</p>

              {delays.length > 0 && (
                <div>
                  <p className="text-sm font-medium flex items-center gap-1 mb-2"><AlertTriangle size={15} className="text-red-600" />Predicted Delays</p>
                  <div className="space-y-2">
                    {delays.map((d, i) => (
                      <div key={i} className="border rounded p-2 flex gap-3" data-testid={`delay-${i}`}>
                        <span className={`px-2 py-0.5 rounded-full text-xs h-fit font-medium ${riskColor(d.risk)}`}>{d.risk}</span>
                        <div><span className="font-mono text-sm">{d.wo}</span><p className="text-xs text-muted-foreground">{d.reason}</p></div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {slipping.length > 0 && (
                <div>
                  <p className="text-sm font-medium flex items-center gap-1 mb-2"><TrendingDown size={15} className="text-amber-600" />Slipping Stages</p>
                  <div className="space-y-1">
                    {slipping.map((s, i) => (
                      <div key={i} className="text-sm" data-testid={`slip-${i}`}><span className="font-medium">{s.stage}:</span> <span className="text-muted-foreground">{s.note}</span></div>
                    ))}
                  </div>
                </div>
              )}

              {recs.length > 0 && (
                <div>
                  <p className="text-sm font-medium flex items-center gap-1 mb-2"><Lightbulb size={15} className="text-blue-600" />Recommendations</p>
                  <ul className="list-disc pl-5 space-y-1">
                    {recs.map((r, i) => <li key={i} className="text-sm text-muted-foreground" data-testid={`rec-${i}`}>{r}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audit history */}
      {history.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><History size={18} className="text-accent" />Audit History</CardTitle></CardHeader>
          <CardContent>
            <div className="divide-y">
              {history.map((h) => (
                <button key={h.id} onClick={() => setAudit(h)} className="w-full text-left py-2 flex items-start justify-between gap-3 hover:bg-muted/30 rounded px-2" data-testid={`history-${h.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm truncate">{h.summary}</p>
                    <p className="text-xs text-muted-foreground">{new Date(h.generated_at).toLocaleString('en-IN')} · {h.active_count} active · {(h.predicted_delays || []).length} risk(s)</p>
                  </div>
                  <span className="text-xs text-accent whitespace-nowrap">View</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Operator performance */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold flex items-center gap-2"><Trophy size={18} className="text-accent" />Operator Leaderboard</h2>
          <div className="flex gap-1">
            <Button size="sm" variant={period === 'all' ? 'default' : 'outline'} onClick={() => setPeriod('all')} data-testid="period-all">All-time</Button>
            <Button size="sm" variant={period === 'week' ? 'default' : 'outline'} onClick={() => setPeriod('week')} data-testid="period-week">This week</Button>
          </div>
        </div>
        {loadingOps ? (
          <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" /></div>
        ) : operators.length === 0 ? (
          <Card><CardContent className="py-8 text-center text-muted-foreground">{period === 'week' ? 'No operator activity in the last 7 days.' : 'No operator activity recorded yet. Advance job stages (with an operator name) to build performance data.'}</CardContent></Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {operators.map((o, i) => (
              <Card key={o.operator} className={i === 0 ? 'ring-2 ring-amber-400' : ''} data-testid={`operator-card-${o.operator}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${rankStyle(i)}`} data-testid={`rank-${o.operator}`}>{i + 1}</div>
                      <div><p className="font-medium leading-tight">{o.operator}</p><Stars n={o.rating} /></div>
                    </div>
                    <span className="text-xs text-muted-foreground">{o.stages_completed} done</span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-muted/40 rounded p-2"><p className="text-[11px] text-muted-foreground">Avg / stage</p><p className="font-mono">{o.avg_minutes} min</p></div>
                    <div className="bg-muted/40 rounded p-2"><p className="text-[11px] text-muted-foreground">Vs team</p><p className={`font-mono ${o.speed_ratio <= 1 ? 'text-green-600' : 'text-red-600'}`}>{o.speed_ratio <= 1 ? `${Math.round((1 - o.speed_ratio) * 100)}% faster` : `${Math.round((o.speed_ratio - 1) * 100)}% slower`}</p></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductionInsightPage;
